import { InputSeries } from "../api/cppmodelApi";

// Point edits on an input series. Times are in milliseconds and kept whole and
// strictly ascending, so the series stays a valid zero-order-hold signal.

const MIN_POINT_GAP_MS = 1;

export const areSeriesEqual = (first: InputSeries | undefined, second: InputSeries | undefined): boolean => {
  if (!first || !second) {
    return first === second;
  }

  return first.label === second.label
    && first.x.length === second.x.length
    && first.y.length === second.y.length
    && first.x.every((value, index) => value === second.x[index])
    && first.y.every((value, index) => value === second.y[index]);
};

export const areSeriesListsEqual = (first: InputSeries[], second: InputSeries[]): boolean => {
  return first.length === second.length
    && first.every((series) => areSeriesEqual(series, second.find((other) => other.label === series.label)));
};

// Round a dragged value to about four significant digits of the visible range,
// so a drag yields 12.35 rather than 12.3467198.
export const roundToRange = (value: number, range: number): number => {
  if (!Number.isFinite(range) || range <= 0) {
    return value;
  }

  const step = Math.pow(10, Math.floor(Math.log10(range)) - 3);
  return Number((Math.round(value / step) * step).toPrecision(12));
};

// Move a point, keeping it between its neighbours so the order never changes.
export const moveInputPoint = (series: InputSeries, index: number, time: number, value: number): InputSeries => {
  const previousTime = index > 0 ? series.x[index - 1] + MIN_POINT_GAP_MS : 0;
  const nextTime = index < series.x.length - 1 ? series.x[index + 1] - MIN_POINT_GAP_MS : Infinity;
  const x = [...series.x];
  const y = [...series.y];

  x[index] = Math.min(Math.max(Math.round(time), previousTime), nextTime);
  y[index] = value;

  return { ...series, x, y };
};

// Insert a point at the given time, or change the value of the point already
// there.
export const insertInputPoint = (series: InputSeries, time: number, value: number): InputSeries => {
  const roundedTime = Math.max(0, Math.round(time));
  const existingIndex = series.x.indexOf(roundedTime);

  if (existingIndex >= 0) {
    return moveInputPoint(series, existingIndex, roundedTime, value);
  }

  const insertIndex = series.x.findIndex((pointTime) => pointTime > roundedTime);
  const at = insertIndex < 0 ? series.x.length : insertIndex;

  return {
    ...series,
    x: [...series.x.slice(0, at), roundedTime, ...series.x.slice(at)],
    y: [...series.y.slice(0, at), value, ...series.y.slice(at)],
  };
};

// Remove a point; a series always keeps at least one.
export const removeInputPoint = (series: InputSeries, index: number): InputSeries => {
  if (series.x.length <= 1) {
    return series;
  }

  return {
    ...series,
    x: series.x.filter((_, pointIndex) => pointIndex !== index),
    y: series.y.filter((_, pointIndex) => pointIndex !== index),
  };
};

// Set a point to an exact time and value (from the points table). The point
// may move past its neighbours, so the series is re-sorted. Returns null when
// another point already sits at that time.
export const setInputPoint = (series: InputSeries, index: number, time: number, value: number): InputSeries | null => {
  const roundedTime = Math.max(0, Math.round(time));

  if (series.x.some((pointTime, pointIndex) => pointIndex !== index && pointTime === roundedTime)) {
    return null;
  }

  const points = series.x.map((pointTime, pointIndex) => (
    pointIndex === index ? [roundedTime, value] : [pointTime, series.y[pointIndex]]
  ));
  points.sort((first, second) => first[0] - second[0]);

  return { ...series, x: points.map(([pointTime]) => pointTime), y: points.map(([, pointValue]) => pointValue) };
};
